import { useSignal } from "@preact/signals";
import { useEffect, useMemo } from "preact/hooks";
import { MaterialIcon, SeedBibleIcon } from "../icons";
import { useI18n } from "../../i18n";
import type { LoginManager } from "../../managers/LoginManager";
import { trimmedOrNull } from "../../managers/Utils";
import type {
  TodayManager,
  TodayPassageTarget,
} from "../../managers/TodayManager";
import { useTimeContext } from "./TimeContext";

/**
 * Stands in for the name while the greeting is translated, so the finished
 * sentence can be split around it. Same trick as the Today header: the name
 * needs its own element for the accent colour, but where it sits is the
 * translation's business. A private-use code point, so it can't collide with
 * anything a translator would write, and the user-supplied name never enters
 * the translated string as markup.
 */
const NAME_PLACEHOLDER = "\uE000";

/** Splits a marked verse on its `<hl>`/`</hl>` boundaries; odd parts were inside. */
const HIGHLIGHT_MARKERS = /<hl>|<\/hl>/;

export const Welcome = (props: {
  today: TodayManager;
  login: LoginManager;
  onOpenBookSelector: () => void;
  onOpenPassage: (target: TodayPassageTarget) => void;
  onTakeTour: () => void;
}) => {
  const {
    bookNames,
    lastTranslationBooks,
    getVerseText,
    lastTranslationId,
    getDefaultTranslation,
  } = props.today;
  // The cached profile is what the sidebar can already show while the account
  // record is still loading. Prefer a real name from either, so the greeting
  // doesn't stay on "Welcome!" until that request finishes — and doesn't stay
  // there if the record arrives with a blank name while the cache has one.
  // Trim first: `??` treats "" and whitespace as a real name and would skip
  // the cache.
  const username =
    trimmedOrNull(props.login.profile.value?.name) ??
    trimmedOrNull(props.login.cachedProfile?.value?.name);
  const { t, language } = useI18n();
  // `TimeProvider` re-renders this subtree every ten seconds so the date stays
  // current; without `tick` below it would be fixed at whenever Today opened.
  const { tick } = useTimeContext();

  const date = useMemo(() => {
    const now = new Date();
    const month = now
      .toLocaleString(language, { month: "short" })
      .toUpperCase();
    return `${now.getDate()} ${month}`;
  }, [language, tick]);

  // `afterName` is undefined when nobody is signed in, or a locale left the
  // placeholder out — that's what decides whether a name element renders.
  const greeting = username
    ? t("personal-greeting", {
        name: NAME_PLACEHOLDER,
        defaultValue: "Welcome {{name}}",
      })
    : t("anonymous-greeting", { defaultValue: "Welcome!" });
  const [beforeName, afterName] = greeting.split(NAME_PLACEHOLDER);

  const welcomeVerse = useSignal("");

  const johnBookName = bookNames.value.get("JHN");
  const firstBook = lastTranslationBooks.value?.books[0];
  // John is the usual welcome passage; translations that lack it (an Old
  // Testament-only translation, say) fall back to the translation's own
  // first book (typically Genesis). Both stay `undefined` until book data
  // loads, rather than guessing, so nothing below renders the wrong book.
  const welcomeBookId = johnBookName !== undefined ? "JHN" : firstBook?.id;
  const welcomeBookName = johnBookName ?? firstBook?.name;
  // Gates the fade-in below: true only once the book is known *and* its
  // opening verse has come back, so the heading and quote reveal together
  // instead of the heading beating the async verse fetch.
  const passageReady =
    welcomeBookName !== undefined && welcomeVerse.value.length > 0;

  useEffect(() => {
    if (welcomeBookId === undefined) {
      return;
    }

    let isActive = true;

    const translationId =
      lastTranslationId.value ?? getDefaultTranslation() ?? "";
    // The table only covers John 1:1 -- once the target has fallen back to a
    // different book, it no longer applies.
    const mappedVerse =
      welcomeBookId === "JHN" ? WELCOME_VERSE_MAP[translationId] : undefined;

    // The table is the common case and needs no network round trip; the API
    // is only a fallback for a translation the table has not been given yet.
    if (mappedVerse !== undefined) {
      welcomeVerse.value = `"${mappedVerse}"`;
    } else {
      void getVerseText(translationId, welcomeBookId, 1, 1).then(
        (rawVerseText) => {
          if (isActive) {
            welcomeVerse.value = `"${rawVerseText ?? ""}"`;
          }
        }
      );
    }

    return () => {
      isActive = false;
    };
  }, [lastTranslationId.value, welcomeBookId]);

  return (
    <div className={"sb-today-welcome-screen"}>
      <div className="sb-today-welcome-screen-heading">
        <span className="sb-today-welcome-screen-date">{date}</span>
        <h1 className={"sb-today-welcome-screen-greeting"}>
          {beforeName}
          {afterName === undefined ? null : (
            <span className="sb-today-welcome-screen-greeting-name">
              {username}
            </span>
          )}
          {afterName}
        </h1>
      </div>
      <span
        className={`sb-today-welcome-screen-book${passageReady ? " sb-today-welcome-screen-passage-visible" : ""}`}
      >
        <span className="sb-today-welcome-screen-reveal">
          {welcomeBookName ? `${welcomeBookName.toUpperCase()} 1:1` : ""}
        </span>
      </span>
      {/*
        Real nodes rather than `dangerouslySetInnerHTML`. The `<hl>` markers are
        author-controlled -- they come only from the table below -- but the
        unmapped fallback path renders text straight from the Bible API through
        this same element, and as raw HTML that text was never escaped.
      */}
      <div
        className={`sb-today-welcome-screen-verse${passageReady ? " sb-today-welcome-screen-passage-visible" : ""}`}
      >
        <div className="sb-today-welcome-screen-reveal">
          {welcomeVerse.value.split(HIGHLIGHT_MARKERS).map((part, index) =>
            index % 2 === 1 ? (
              <span
                className="sb-today-welcome-screen-verse-highlight"
                key={index}
              >
                {part}
              </span>
            ) : (
              part
            )
          )}
        </div>
      </div>
      <div className="sb-today-welcome-screen-actions">
        <div className={"sb-today-welcome-screen-navigation"}>
          <button
            className="sb-today-book-selector-button sb-today-clickable"
            type="button"
            onClick={props.onOpenBookSelector}
          >
            <SeedBibleIcon
              className="sb-today-seed-bible-icon"
              style={{
                width: "1.25rem",
                height: "1.25rem",
                fill: "var(--sb-reader-font-color)",
              }}
            />
            {t("open-bible", { defaultValue: "Open Bible" })}
          </button>
          <button
            className={
              "sb-today-welcome-screen-start-button sb-today-clickable"
            }
            disabled={welcomeBookId === undefined}
            onClick={() => {
              if (welcomeBookId === undefined) {
                return;
              }
              // `onOpenPassage` falls back to the default translation when unset.
              props.onOpenPassage({
                bookId: welcomeBookId,
                chapter: 1,
                translationId: lastTranslationId.value,
              });
            }}
          >
            <span className="sb-today-welcome-screen-reveal">
              {welcomeBookId !== undefined
                ? t("read-book-chapter-button", {
                    defaultValue: "Read {{bookName}} {{chapterNumber}}",
                    bookName: welcomeBookName,
                    chapterNumber: 1,
                  })
                : t("read-the-bible", { defaultValue: "Read the Bible" })}
              <MaterialIcon>arrow_right_alt</MaterialIcon>
            </span>
          </button>
        </div>
        <button
          className="sb-today-welcome-screen-tour-button sb-today-clickable"
          type="button"
          onClick={props.onTakeTour}
        >
          {t("take-a-tour", { defaultValue: "Take a tour" })}
        </button>
      </div>
    </div>
  );
};

/**
 * Maps a `translationId` to its pre-highlighted John 1:1 text.
 *
 * Keyed by exactly the unique translation IDs declared in
 * `DEFAULT_TRANSLATIONS_BY_LANGUAGE` (`BibleReadingManager`). Each value is the
 * verse text from the Free Use Bible API (helloao) with `<hl>…</hl>` tags
 * wrapped around the two semantic anchors of the verse:
 *   - the word for "beginning" (e.g. "principio", "commencement"), and
 *   - the clause "the Word was God" (e.g. "el Verbo era Dios").
 */
type WelcomeVerseMap = Record<string, string>;

const WELCOME_VERSE_MAP: WelcomeVerseMap = {
  // English — AAB (Ancients Accessible Bible)
  AAB: "In the <hl>beginning</hl> was the Word, and the Word was with God, and the <hl>Word was God.</hl>",
  // Amharic — amh_amh
  amh_amh:
    "<hl>በመጀመሪያው</hl> ቃል ነበረ፥ ቃልም በእግዚአብሔር ዘንድ ነበረ፥ <hl>ቃልም እግዚአብሔር ነበረ።</hl>",
  // Arabic — ARBNAV (New Arabic Version)
  ARBNAV:
    "فِي <hl>الْبَدْءِ</hl> كَانَ الْكَلِمَةُ، وَالْكَلِمَةُ كَانَ عِنْدَ اللهِ. <hl>وَكَانَ الْكَلِمَةُ اللهُ.</hl>",
  // Bengali — ben_ocv
  ben_ocv:
    "<hl>আদিতে</hl> বাক্য ছিলেন, সেই বাক্য ঈশ্বরের সঙ্গে ছিলেন এবং <hl>বাক্যই ঈশ্বর ছিলেন।</hl>",
  // Spanish — spa_onbv (Open Nueva Biblia Viva)
  spa_onbv:
    "<hl>Antes que nada existiera</hl>, ya existía la Palabra, y la Palabra estaba con Dios porque <hl>aquel que es la Palabra era Dios.</hl>",
  // Persian — pes_opcb
  pes_opcb:
    "در <hl>آغاز</hl> کلمه بود، کلمه با خدا بود، <hl>و کلمه، خدا بود.</hl>",
  // French — fra_ncl (néo-Crampon Libre)
  fra_ncl:
    "Au <hl>commencement</hl> était le Verbe, et le Verbe était en Dieu, et le <hl>Verbe était Dieu.</hl>",
  // Hindi — hin_cvb
  hin_cvb:
    "<hl>आदि</hl> में वचन था, वचन परमेश्वर के साथ था और <hl>वचन परमेश्वर था.</hl>",
  // Indonesian — ind_ayt
  ind_ayt:
    "Pada <hl>mulanya</hl> adalah Firman, Firman itu bersama-sama dengan Allah dan <hl>Firman itu adalah Allah.</hl>",
  // Japanese — jpn_loc
  jpn_loc:
    "<hl>初め</hl>に、ことばがあった。ことばは神とともにあった。<hl>ことばは神であった。</hl>",
  // Korean — kor_old
  kor_old:
    "<hl>태초</hl>에 말씀이 계시니라 이 말씀이 하나님과 함께 계셨으니 <hl>이 말씀은 곧 하나님이시니라</hl>",
  // Nepali — npi_ncb
  npi_ncb:
    "<hl>आदिमा</hl> वचन हुनुहुन्थ्यो र वचन परमेश्‍वरसँग हुनुहुन्थ्यो। अनि <hl>वचन परमेश्‍वर हुनुहुन्थ्यो।</hl>",
  // Portuguese — por_onbv (Open Nova Bíblia Viva)
  por_onbv:
    "No <hl>princípio</hl> era aquele que é a Palavra, e ele estava com Deus e <hl>era Deus.</hl>",
  // Russian — rus_syn (Synodal)
  rus_syn:
    "В <hl>начале</hl> было Слово, и Слово было у Бога, и <hl>Слово было Бог.</hl>",
  // Swahili — swh_onmm
  swh_onmm:
    "Hapo <hl>mwanzo</hl> alikuwako Neno, naye huyo Neno alikuwa pamoja na Mungu, naye <hl>Neno alikuwa Mungu.</hl>",
  // Turkish — tur_ytc
  tur_ytc:
    "<hl>Başlangıçta</hl> Söz vardı ve Söz Tanrı'yla birlikteydi ve <hl>Söz Tanrı’ydı.</hl>",
  // Uyghur — uig_ara
  uig_ara:
    "<hl>مۇقەددەمدە</hl> «كالام» بار ئىدى؛ كالام خۇدا بىلەن بىللە ئىدى ھەم <hl>كالام خۇدا ئىدى.</hl>",
  // Ukrainian — ukr_ufb
  ukr_ufb:
    "У <hl>починї</hl> було Слово, й Слово було в Бога, й <hl>Бог було Слово.</hl>",
  // Urdu — urd_oucv
  urd_oucv:
    "<hl>اِبتدا</hl> میں کلام تھا اَور کلام خُدا کے ساتھ تھا اَور <hl>کلام خُدا ہی تھا۔</hl>",
  // Vietnamese — vie_vcb
  vie_vcb:
    "<hl>Ban đầu</hl> có Ngôi Lời, Ngôi Lời ở với Đức Chúa Trời, và <hl>Ngôi Lời là Đức Chúa Trời.</hl>",
  // Chinese (Mandarin) — cmn_cbt
  cmn_cbt: "<hl>太初</hl>，道已經存在，道與上帝同在，<hl>道就是上帝。</hl>",
};
